import { useState, useEffect } from "react";
import { Phone, Mail, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function Tilbud() {
  const [formStatus, setFormStatus] = useState({ type: null, message: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    beskrivelse: "",
    website_url: "", // Honeypot
  });

  useEffect(() => {
    document.title = "Indhent tilbud - BMT TAG ApS";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Indhent et uforpligtende tilbud på tagdækning eller tagpap hos BMT TAG ApS. Vi udfører professionelt tagarbejde for private og erhverv.");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: null, message: null });

    if (!formData.navn || !formData.email || !formData.telefon) {
      setFormStatus({
        type: "error",
        message: "Venligst udfyld alle påkrævede felter (Navn, Telefon og Email).",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: "success",
          message: "Mange tak for din henvendelse! Vi har modtaget din tilbudsforespørgsel og vender tilbage hurtigst muligt.",
        });
        setFormData({
          navn: "",
          telefon: "",
          email: "",
          beskrivelse: "",
          website_url: "",
        });
      } else {
        setFormStatus({
          type: "error",
          message: "Der skete en fejl ved afsendelsen. Prøv venligst igen eller kontakt os direkte på tlf. 23 28 71 83.",
        });
      }
    } catch (err) {
      console.error("Formularfejl:", err);
      setFormStatus({
        type: "error",
        message: "Netværksfejl. Tjek venligst din internetforbindelse og prøv igen.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-20">
      {/* Hero / Header Sektion */}
      <section className="relative bg-brand-blue-dark text-white py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/modern-danish-roof-roofing.jpg"
            alt="Indhent tilbud"
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-brand-blue-dark/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl font-display">
            Indhent et uforpligtende tilbud
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Udfyld formularen herunder, så vender vi tilbage med et uforpligtende tilbud på din tagopgave.
          </p>
        </div>
      </section>

      {/* Formular og info */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Formular (venstre) */}
          <div className="lg:col-span-8 bg-white rounded-xl border border-brand-neutral-border p-8 shadow-sm space-y-6">
            <h2 className="text-2xl font-bold text-brand-blue-dark">
              Oplysninger om opgaven
            </h2>

            {formStatus.message && (
              <div
                className={`p-4 rounded-md text-sm ${
                  formStatus.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {formStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot - ABSOLUT SKJULT */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="navn" className="text-xs font-bold uppercase text-brand-blue-dark">
                    Navn <span className="text-brand-accent">*</span>
                  </label>
                  <input
                    type="text"
                    id="navn"
                    name="navn"
                    required
                    value={formData.navn}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                    placeholder="Dit fulde navn"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="telefon" className="text-xs font-bold uppercase text-brand-blue-dark">
                    Telefonnummer <span className="text-brand-accent">*</span>
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    required
                    value={formData.telefon}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                    placeholder="Tlf. nr."
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="text-xs font-bold uppercase text-brand-blue-dark">
                  E-mail <span className="text-brand-accent">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                  placeholder="din@email.dk"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="beskrivelse" className="text-xs font-bold uppercase text-brand-blue-dark">
                  Beskrivelse af opgaven
                </label>
                <textarea
                  id="beskrivelse"
                  name="beskrivelse"
                  rows="6"
                  value={formData.beskrivelse}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                  placeholder="Beskriv din opgave, f.eks. tagtype, areal, hældning osv."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-brand-accent py-3 text-sm font-bold text-white shadow-sm hover:bg-brand-accent-hover transition-colors disabled:bg-slate-400 cursor-pointer"
              >
                {isSubmitting ? "Sender..." : "Send tilbudsforespørgsel"}
              </button>
            </form>
          </div>

          {/* Info sidebar (højre) */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-brand-blue text-white rounded-xl p-6 shadow-sm space-y-6">
              <h3 className="text-lg font-bold font-display border-b border-white/10 pb-3">
                Direkte kontakt
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Du er også altid velkommen til at ringe eller skrive direkte til os for en snak om din opgave.
              </p>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2.5">
                  <Phone className="h-5 w-5 text-brand-accent shrink-0" />
                  <a href="tel:+4523287183" className="hover:text-brand-accent transition-colors whitespace-nowrap text-slate-200 font-semibold">
                    23 28 71 83
                  </a>
                </div>
                <div className="flex items-center gap-2.5">
                  <Mail className="h-5 w-5 text-brand-accent shrink-0" />
                  <a href="mailto:mb@bmttag.dk" className="hover:text-brand-accent transition-colors whitespace-nowrap text-slate-200 font-semibold">
                    mb@bmttag.dk
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-brand-neutral-light rounded-xl border border-brand-neutral-border p-6 space-y-4">
              <h4 className="text-sm font-bold text-brand-blue-dark uppercase tracking-wider">
                Vores Garanti & Tryghed
              </h4>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span>10 års tredobbelt forsikringsdækket garanti</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span>Eller 15 års produktgaranti på visse materialer</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span>Medlem af Dansk Byggeri & INTA</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
